// Centralized JSON fetch utility with HTML fallback detection & transient error classification.
// This prevents components/hooks from manually parsing and duplicating logic.

export class TransientHtmlError extends Error {
  snippet: string;
  status?: number;
  constructor(message: string, snippet: string, status?: number) {
    super(message);
    this.name = 'TransientHtmlError';
    this.snippet = snippet;
    this.status = status;
  }
}

export class TransientParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransientParseError';
  }
}

export class HttpError extends Error {
  status: number;
  bodySnippet?: string;
  constructor(status: number, message: string, bodySnippet?: string) {
    super(message);
    this.status = status;
    this.name = 'HttpError';
    this.bodySnippet = bodySnippet;
  }
}

const HTML_REGEX = /<!DOCTYPE|<html|<head|<body/i;
let loggedHtmlSignature = false;

function normalizePath(path: string): string {
  // Remove duplicate slashes (except protocol) and trailing slash (except root or explicit api root)
  let p = path.trim();
  if (!p.startsWith('http')) {
    if (!p.startsWith('/')) p = '/' + p;
  }
  // collapse multiple slashes
  p = p.replace(/([^:])\/+/g, '$1/');
  // keep trailing slash only for root or explicit collection endpoints we know need it
  if (p.length > 1 && p.endsWith('/')) {
    // Special-case known collection endpoints if you want to preserve – currently we standardize by removing
    p = p.slice(0, -1);
  }
  return p;
}

interface FetchJsonOptions extends RequestInit {
  expect?: 'json' | 'text' | 'auto';
  retryHtml?: boolean; // reserved for future external retry loops
}

export async function fetchJson<T = any>(path: string, opts: FetchJsonOptions = {}): Promise<T> {
  const { expect = 'json', headers, ...rest } = opts;
  const url = normalizePath(path);
  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json, text/plain;q=0.8, */*;q=0.5',
    'Content-Type': 'application/json',
    ...(headers as any),
  };

  const res = await fetch(url, { ...rest, headers: mergedHeaders });

  const status = res.status;
  const ct = (res.headers.get('content-type') || '').toLowerCase();

  // Handle non-success early for 4xx (unless maybe body still JSON for details)
  if (status >= 400 && status < 500) {
    // Try parse JSON error
    let snippet = '';
    try {
      const text = await res.text();
      snippet = text.slice(0, 160).replace(/\s+/g, ' ');
      if (HTML_REGEX.test(text)) {
        throw new HttpError(status, `Client error ${status} (HTML fallback)`, snippet);
      }
      try {
        const parsed = JSON.parse(text);
        throw new HttpError(status, parsed?.message || `Client error ${status}`, snippet);
      } catch {
        throw new HttpError(status, `Client error ${status}`, snippet);
      }
    } catch (e) {
      if (e instanceof HttpError) throw e;
      throw new HttpError(status, `Client error ${status}`, snippet);
    }
  }

  // 5xx – still attempt JSON parse but may classify transient
  if (status >= 500) {
    const text = await res.text();
    const snippet = text.slice(0, 160).replace(/\s+/g, ' ');
    if (HTML_REGEX.test(text)) {
      if (!loggedHtmlSignature) {
        console.groupCollapsed('[fetchJson] 5xx HTML snippet');
        console.log(snippet);
        console.groupEnd();
        loggedHtmlSignature = true;
      }
      throw new TransientHtmlError('Server returned HTML (5xx)', snippet, status);
    }
    try {
      const parsed = JSON.parse(text);
      // treat as HttpError but maybe transient depending on caller retry policy
      throw new HttpError(status, parsed?.message || `Server error ${status}`, snippet);
    } catch {
      throw new HttpError(status, `Server error ${status}`, snippet);
    }
  }

  // Happy JSON path
  if (ct.includes('application/json')) {
    try {
      return (await res.json()) as T;
    } catch (e: any) {
      throw new TransientParseError(`JSON parse failed: ${e?.message || e}`);
    }
  }

  // Non-declared JSON: sniff
  const text = await res.text();
  if (HTML_REGEX.test(text)) {
    const snippet = text.slice(0, 160).replace(/\s+/g, ' ');
    if (!loggedHtmlSignature) {
      console.groupCollapsed('[fetchJson] HTML fallback snippet');
      console.log(snippet);
      console.groupEnd();
      loggedHtmlSignature = true;
    }
    throw new TransientHtmlError('Received HTML instead of JSON', snippet, status);
  }
  if (expect === 'text') return text as any as T;

  // Try parse as JSON anyway
  try {
    return JSON.parse(text) as T;
  } catch (e: any) {
    throw new TransientParseError(`Undeclared JSON parse failed: ${e?.message || e}`);
  }
}

export function isTransientError(err: unknown): boolean {
  return err instanceof TransientHtmlError || err instanceof TransientParseError;
}
